import { Box, CircularProgress, Divider, List, Typography } from "@mui/material";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";

export type InfiniteScrollListProps<T> = {
    items: T[];
    hasNextPage: boolean;
    isLoading: boolean;
    onLoadMore: () => void;
    renderItem: (item: T, index: number) => React.ReactNode;
    emptyComponent?: React.ReactNode;
    className?: string;
    maxHeight?: number | string;
    triggerDistance?: number; // 200px gibi özel tetikleme mesafesi
    skeletonComponent: React.ReactNode;
    dense?: boolean;
};

function InfiniteScrollList<T>({
    items,
    hasNextPage,
    isLoading,
    onLoadMore,
    renderItem,
    emptyComponent,
    className,
    maxHeight = "400px",
    triggerDistance = 200, // Default 200px kala tetikle
    skeletonComponent,
    dense = true,
}: InfiniteScrollListProps<T>) {
    const [isFetching, setIsFetching] = useState(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const handleLoadMore = useCallback(async () => {
        if (isFetching || !hasNextPage) return;

        setIsFetching(true);
        await onLoadMore();
        setIsFetching(false);
    }, [isFetching, hasNextPage, onLoadMore]);

    // Intersection Observer - tek yöntem
    useEffect(() => {
        const currentLoadMoreRef = loadMoreRef.current;
        const currentScrollContainer = scrollContainerRef.current;

        if (!currentLoadMoreRef || !hasNextPage || !currentScrollContainer) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && !isLoading && !isFetching) {
                    handleLoadMore();
                }
            },
            {
                root: currentScrollContainer, // root element
                threshold: 0.1,
                rootMargin: `0px 0px ${triggerDistance}px 0px`, // trigger margin
            }
        );

        observerRef.current.observe(currentLoadMoreRef);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasNextPage, isLoading, isFetching, handleLoadMore, triggerDistance]);

    if (items.length === 0 && !isLoading) {
        return (
            emptyComponent || (
                <Box display="flex" justifyContent="center" height={"100%"} alignItems="center" p={2}>
                    <Typography variant="body2" color="text.secondary">
                        Veri bulunamadı
                    </Typography>
                </Box>
            )
        );
    }

    return (
        <Box
            ref={scrollContainerRef}
            className={className}
            sx={{
                width: "100%",
                height: "100%",
                minHeight: "400px",
                overflow: "auto",
                padding: 0,
                maxHeight,
            }}
        >
            <List dense={dense} sx={{ padding: "0px !important" }}>
                {isLoading && !items.length ? (
                    skeletonComponent
                ) : (
                    <>
                        {items.map((item, index) => (
                            <Fragment key={index}>
                                {renderItem(item, index)} <Divider />
                            </Fragment>
                        ))}

                        {(isLoading || isFetching) && (
                            <div className=" py-4 grid place-content-center">
                                <CircularProgress size={25} color="primary" />
                            </div>
                        )}

                        {hasNextPage && !isLoading && !isFetching && (
                            <div
                                ref={loadMoreRef}
                                style={{
                                    height: "1px",
                                    visibility: "hidden",
                                    pointerEvents: "none",
                                }}
                            />
                        )}
                    </>
                )}
            </List>
        </Box>
    );
}

export default InfiniteScrollList;
